import React, { useCallback, useContext, useState, useEffect } from 'react';
import _ from 'lodash';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { TranslatorContext } from '@jutro/locale';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import { messages as paymentMessages } from 'gw-capability-policychange-common-react';
import PackageInfoComponent from '../../components/PackageInfo/PackageInfoComponent';
import messages from './ItemInfoPage.messages'
import metadata from './ItemInfoPage.metadata.json5';
import './ItemInfoPage.messages';
import styles from './ItemInfoPage.module.scss';
import {GatewayEndorsementService as EndorsementService} from 'gw-capability-gateway-policychange';


function ItemInfoPage(props) {
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const {
        wizardData, updateWizardData, isNewSubmission, authHeader, additionalWizardData, updateAdditionalWizardData
    } = props;
    const { submissionVM } = wizardData;
    const {
        initialValidation,
        isComponentValid,
        onValidate
    } = useValidation('ItemInfo');
    const [accountContacts, setAccountContacts] = useState([]);
    const [policyLocations, setPolicyLocations] = useState([]);
    const [isPageInitialized, setPageInitialized] = useState(false);
    const nonValidatedForms  = ['miscitem_ey','seatrailers_ey','marine_ey'];
    const nonValidatedCoveragePart = ['nonpackage_ey'];


    useEffect( () => {   
        if(!isPageInitialized) {
            _.each(submissionVM.lobData.homeowners.allCoverables.children, (cov, index)=> {
                if(!cov.isPrimary.value && additionalWizardData && additionalWizardData.packageInfoDetails && additionalWizardData.packageInfoDetails[cov.publicID.value]) {
                    cov.location.value = {};
                }
            }); 
            if(policyLocations.length== 0)  {  
                EndorsementService.getPolicyLocations(
                    [submissionVM.jobID.value],
                    authHeader
                ).then((locations) => {
                    const _locations = locations.map((c) => {
                        c['id'] = c.publicID;
                        return c
                    });
                    setPolicyLocations(_locations);
                }); 
        } 

        if(accountContacts.length == 0){
                EndorsementService.getAccountContacts(
                    submissionVM.jobID.value,
                    authHeader
                ).then((contacts) => {
                    const _contacts = contacts.map((c) => {
                        c['id'] = c.publicID;
                        return c
                    });
                    _contacts.push({ id: "placeholder", displayName: translator(messages.searchContact) });
                    setAccountContacts(_contacts);
                })
            }
            updateWizardData(wizardData);
            setPageInitialized(true);
        }
    }, [EndorsementService, authHeader, submissionVM, additionalWizardData, isPageInitialized, wizardData]);

    const updateLocations = (locations) => {
        const _locations = locations.map((c) => {
            c['id'] = c.publicID;
            return c
        });
        setPolicyLocations(_locations);
    }

    const updateAccountContacts = (contacts) => {
        const _contacts = contacts.map((c) => {
            c['id'] = c.publicID;
            return c
        });
        _contacts.push({ id: "placeholder", displayName: "Search Existing Contacts" });
        setAccountContacts(_contacts);
    }

    const updatePackage = (value, index) => {
        const newSubmissionVM = viewModelService.clone(submissionVM);
        _.set(newSubmissionVM, `lobData.homeowners.allCoverables.children.${index}.value`, value);
        _.set(wizardData, 'submissionVM', newSubmissionVM);
        updateWizardData(wizardData);
    }

    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVM, path, value);
            updateWizardData(wizardData);
        },
        [submissionVM, updateWizardData, wizardData]
    );

    const onNext = useCallback(async () => {
        submissionVM.value = await EndorsementService.saveEndorsement(
            [submissionVM.value],
            authHeader
        );
        return wizardData;
    }, [EndorsementService, authHeader, submissionVM, wizardData]);


    const generateOverrides = () => {
        const overrides = submissionVM.lobData.homeowners.allCoverables.children.map((item, index) => {
            const required = !(nonValidatedCoveragePart.includes(item.coveragePart.value.code) &&
                            nonValidatedForms.includes(item.coverageForm.value.code))
            item.isHoValidationRequired.value = required;
            item.omiaDetail.isValidationRequired.value = required;
            return {
                [`packageAccordionCard${index}`]: {
                    header: `Package ${item.dwellingCounter.value} - ${translator({ id: item['omiaDetail'].coverageForm.value.name })} ${translator({ id: item['omiaDetail'].coveragePart.value.name })}`,
                    className: 'packageAccordionHeader',
                    cardHeadingClassName:"packageAccordionHeading",
                    chevronAlignment:"right"
                },
                [`packageInfoComponent${index}`]: {
                    policyLocations: policyLocations,
                    accountContacts: accountContacts,                    
                    authHeader: authHeader,
                    coverableVM: item,
                    updateLocations: updateLocations,
                    updateAccountContacts:updateAccountContacts,
                    jobID: _.get(submissionVM, 'jobID.value'),
                    accountNumber: _.get(submissionVM, 'baseData.accountNumber.value'),
                    updatePackage: (coverable) => {
                        updatePackage(coverable, index);
                    },
                    onValidate:onValidate,
                    packageIndex:index,
                    additionalWizardData,
                    updateAdditionalWizardData,
                    isSubmission: isNewSubmission === 'true'
                }
            };
        });
        return Object.assign({}, ...overrides);
    }

    const overrideProps = {
        '@field': {
            labelPosition: 'left',
            onValueChange: writeValue
        },
        ...generateOverrides()
    };

    const resolvers = {
        resolveCallbackMap: {},
        resolveComponentMap: {
            packageInfoComponent:PackageInfoComponent,
        },
    };

    return (
        <WizardPage
            onNext={onNext}
            disableNext={!submissionVM.lobData.homeowners.allCoverables.aspects.subtreeValid}
            skipWhen={initialValidation}
            cancelLabel={translator(appConfig.persona === 'policyholder' ? paymentMessages.cancelAllChanges : paymentMessages.cancel)}
            showPrevious
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={styles}
            />
        </WizardPage>
    );
}

ItemInfoPage.propTypes = wizardProps;
export default withAuthenticationContext(ItemInfoPage);
